import React from 'react';

import {
  BellRing,
  CalendarClock,
  CircleCheck,
  CirclePlus,
  CircleX,
  FileUp,
  Flag,
  Mail,
  MessageSquareWarning,
  PenLine,
  PlayCircle,
  RefreshCw,
  RotateCcw,
  Send,
  Signature,
  SignpostBig,
  Undo2,
  UserRound,
} from 'lucide-react';
import { Apollo } from 'src/gql/generated';
import { appColors } from 'src/theme';

const GREEN = '#12B066';
const BLUE = appColors.primary;
const AMBER = '#E87A14';
const SLATE = appColors.iconsType;
const RED = '#D93025';

interface StatusEventMeta {
  color: string;
  renderIcon: (size: number, color: string) => JSX.Element;
  /** Predicate placed after the actor name, e.g. "completed the task". */
  verb: string;
}

/**
 * Copy + icon for each lifecycle event rendered as a flat status row in the feed.
 * Matches the Figma "Updates" treatment: completed = green filled check, assignment
 * = blue user, etc. Exhaustive over ActivityStatusEventType so new events fail the
 * type-check until given copy here.
 */
export const STATUS_EVENT_META: Record<Apollo.ActivityStatusEventType, StatusEventMeta> = {
  [Apollo.ActivityStatusEventType.TaskCreated]: {
    color: BLUE,
    renderIcon: (size, color) => <CirclePlus color={color} size={size} />,
    verb: 'created the task',
  },
  [Apollo.ActivityStatusEventType.TaskAssigned]: {
    color: BLUE,
    renderIcon: (size, color) => <UserRound color={color} size={size} />,
    verb: 'assigned the task',
  },
  [Apollo.ActivityStatusEventType.TaskCompleted]: {
    color: GREEN,
    renderIcon: (size, color) => <CircleCheck color={color} size={size} />,
    verb: 'completed the task',
  },
  [Apollo.ActivityStatusEventType.TaskReopened]: {
    color: AMBER,
    renderIcon: (size, color) => <RotateCcw color={color} size={size} />,
    verb: 'reopened the task',
  },
  [Apollo.ActivityStatusEventType.TaskPhaseChanged]: {
    color: BLUE,
    renderIcon: (size, color) => <SignpostBig color={color} size={size} />,
    verb: 'moved the task to a new phase',
  },
  [Apollo.ActivityStatusEventType.TaskEdited]: {
    color: SLATE,
    renderIcon: (size, color) => <PenLine color={color} size={size} />,
    verb: 'edited the task',
  },
  [Apollo.ActivityStatusEventType.TaskChangesRequested]: {
    color: AMBER,
    renderIcon: (size, color) => <MessageSquareWarning color={color} size={size} />,
    verb: 'requested changes',
  },
  [Apollo.ActivityStatusEventType.TaskReReviewRequested]: {
    color: BLUE,
    renderIcon: (size, color) => <RefreshCw color={color} size={size} />,
    verb: 'requested re-review',
  },
  [Apollo.ActivityStatusEventType.TaskChangesReminderSent]: {
    color: AMBER,
    renderIcon: (size, color) => <BellRing color={color} size={size} />,
    verb: 'sent a reminder',
  },
  [Apollo.ActivityStatusEventType.TaskReviewReminderSent]: {
    color: BLUE,
    renderIcon: (size, color) => <BellRing color={color} size={size} />,
    verb: 'reminded the reviewers',
  },
  [Apollo.ActivityStatusEventType.TaskSubmittedForReview]: {
    color: BLUE,
    renderIcon: (size, color) => <Send color={color} size={size} />,
    verb: 'requested review',
  },
  [Apollo.ActivityStatusEventType.TaskReviewRequestWithdrawn]: {
    color: SLATE,
    renderIcon: (size, color) => <Undo2 color={color} size={size} />,
    verb: 'took back the review request',
  },
  [Apollo.ActivityStatusEventType.TaskApproved]: {
    color: GREEN,
    renderIcon: (size, color) => <CircleCheck color={color} size={size} />,
    verb: 'approved the task',
  },
  [Apollo.ActivityStatusEventType.TaskStarted]: {
    color: BLUE,
    renderIcon: (size, color) => <PlayCircle color={color} size={size} />,
    verb: 'started the task',
  },
  [Apollo.ActivityStatusEventType.TaskMissingAssignee]: {
    color: AMBER,
    renderIcon: (size, color) => <MessageSquareWarning color={color} size={size} />,
    verb: 'started a task with reviewers but nobody to do the work',
  },
  [Apollo.ActivityStatusEventType.TaskRejected]: {
    color: RED,
    renderIcon: (size, color) => <CircleX color={color} size={size} />,
    verb: 'rejected the task',
  },
  [Apollo.ActivityStatusEventType.TaskInvitationSent]: {
    color: BLUE,
    renderIcon: (size, color) => <Mail color={color} size={size} />,
    verb: 'sent the task to external assignees',
  },
  [Apollo.ActivityStatusEventType.TaskFileUploaded]: {
    color: BLUE,
    renderIcon: (size, color) => <FileUp color={color} size={size} />,
    verb: 'uploaded a file',
  },
  [Apollo.ActivityStatusEventType.TaskAssigneeCompleted]: {
    color: GREEN,
    renderIcon: (size, color) => <CircleCheck color={color} size={size} />,
    verb: 'completed their part of the task',
  },
  [Apollo.ActivityStatusEventType.TaskAssigneeRejected]: {
    color: SLATE,
    renderIcon: (size, color) => <CircleX color={color} size={size} />,
    verb: 'rejected the task as an optional assignee',
  },
  [Apollo.ActivityStatusEventType.TaskAssigneeCompletionUndone]: {
    color: AMBER,
    renderIcon: (size, color) => <Undo2 color={color} size={size} />,
    verb: 'took back their completion',
  },
  [Apollo.ActivityStatusEventType.WorkflowCreated]: {
    color: BLUE,
    renderIcon: (size, color) => <CirclePlus color={color} size={size} />,
    verb: 'created the workflow',
  },
  [Apollo.ActivityStatusEventType.WorkflowStarted]: {
    color: BLUE,
    renderIcon: (size, color) => <PlayCircle color={color} size={size} />,
    verb: 'started the workflow',
  },
  [Apollo.ActivityStatusEventType.WorkflowCompleted]: {
    color: GREEN,
    renderIcon: (size, color) => <CircleCheck color={color} size={size} />,
    verb: 'completed the workflow',
  },
  [Apollo.ActivityStatusEventType.WorkflowPriorityChanged]: {
    color: AMBER,
    renderIcon: (size, color) => <Flag color={color} size={size} />,
    verb: 'changed the priority',
  },
  [Apollo.ActivityStatusEventType.WorkflowOwnerChanged]: {
    color: BLUE,
    renderIcon: (size, color) => <UserRound color={color} size={size} />,
    verb: 'changed the owner',
  },
  [Apollo.ActivityStatusEventType.WorkflowSentForSignature]: {
    color: BLUE,
    renderIcon: (size, color) => <Send color={color} size={size} />,
    verb: 'sent the workflow for signature',
  },
  [Apollo.ActivityStatusEventType.WorkflowRevertedToDraft]: {
    color: AMBER,
    renderIcon: (size, color) => <Undo2 color={color} size={size} />,
    verb: 'reverted the workflow to draft',
  },
  [Apollo.ActivityStatusEventType.WorkflowSigned]: {
    color: GREEN,
    renderIcon: (size, color) => <Signature color={color} size={size} />,
    verb: 'signed the workflow',
  },
};

/** Fallback used if the server ever sends an event we have not mapped yet. */
export const FALLBACK_STATUS_META: StatusEventMeta = {
  color: SLATE,
  renderIcon: (size, color) => <CalendarClock color={color} size={size} />,
  verb: 'updated this',
};
